import { useApi } from '@gravitate-js/the-armory'
import { getAPIURL } from '@utils/api'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { MetadataResponse, OverviewResponse } from '@api/useDriverCards/response'
import type { AddCardsRequest, DeleteRequest, UpsertRequest } from '@api/useDriverCards/request'

export const useDriverCards = () => {
  const api = useApi({ baseURLOverride: getAPIURL() })
  const queryClient = useQueryClient()

  const getDriverCardsOverviewQuery = () =>
    useQuery({
      queryKey: ['driverCardsOverviewQuery'],
      queryFn: () => api.post('cards/driver/overview') as OverviewResponse[],
    })

  const getDriverCardsMetadataQuery = () =>
    useQuery({
      queryKey: ['driverCardsMetadataQuery'],
      queryFn: () => api.post('cards/driver/metadata') as MetadataResponse,
    })

  const addCardsMutation = useMutation({
    mutationKey: ['addCardsMutation'],
    mutationFn: (request: AddCardsRequest) => api.post('cards/driver/bulk_add', request),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: ['driverCardsOverviewQuery'],
      })
    },
  })

  const editDriverCardMutation = useMutation({
    mutationKey: ['editDriverCardMutation'],
    mutationFn: (request: UpsertRequest) => api.post('cards/driver/upsert', request),
    onMutate: async (request) => {
      const oldData = queryClient.getQueryData<OverviewResponse[]>(['driverCardsOverviewQuery'])
      queryClient.setQueriesData<OverviewResponse[]>({ queryKey: ['driverCardsOverviewQuery'] }, (prevData) => {
        return {
          ...prevData,
          rows: prevData?.rows?.map((row) => {
            if (
              row.driver_id === request.driver_id &&
              row.card_id === request.card_id &&
              row.terminal_id === request.terminal_id
            ) {
              return {
                ...row,
                certification_date: request.certification_date,
              }
            }
            return row
          }),
        }
      })
      return { oldData }
    },
    onError: async (_error, _request, context) => {
      queryClient.setQueriesData({ queryKey: ['driverCardsOverviewQuery'] }, context.oldData)
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: ['driverCardsOverviewQuery'],
      })
    },
  })

  const deleteDriverCardMutation = useMutation({
    mutationKey: ['deleteDriverCardMutation'],
    mutationFn: (request: DeleteRequest) => api.post('cards/driver/remove', request),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: ['driverCardsOverviewQuery'],
      })
    },
  })

  return {
    getDriverCardsOverviewQuery,
    getDriverCardsMetadataQuery,
    addCardsMutation,
    editDriverCardMutation,
    deleteDriverCardMutation,
  }
}
